import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { splitAt, compose, join, drop, concat } from 'ramda'
import strings from 'utils/strings.js'
import { CHEC_MAX_VALUE } from 'utils/constants'
import { formatTimeForApi } from 'components/SupportParameterDisplay/utils.js'
import {
  parseHmsStrings,
  GERERAL_SHORT_TIME_FORMAT,
  formatTimeForInput,
} from 'utils/timers'
import { classNames } from 'utils/utils'
import { isNumeric } from 'utils/validation'
import 'styles/InputTimer.css'
import InputLabel from 'components/patterns/Inputs/InputLabel'

const MAX_TIMER_VALUE = 384399 // 999999

const destructureTimeValue = (time) => {
  const [hm, seconds] = splitAt(-3, time)
  const [hours, minutes] = splitAt(-2, hm)

  const addDecimal = compose(join('.'), splitAt(-1))

  return {
    hours,
    minutes,
    seconds: addDecimal(seconds),
  }
}

export default class InputTimer extends Component {
  constructor(props) {
    super(props)

    const isValid = this.determineValidity(props.value)
    this.state = {
      isValid,
      focused: false,
      // we must use state because the user can enter values into time slots
      // (minutes, seconds) that are > 60. If we were to use the value prop
      // then the number in the timer would be auto-converted to the 'proper' value
      time: formatTimeForInput(props.value),
      prevValue: props.value,
    }

    this.inputTimerRef = React.createRef()
  }

  // need to clear state when value gets reset from outside of component
  // for example: from a clear button
  static getDerivedStateFromProps(props, state) {
    if (
      (props.value === 0 || props.value === GERERAL_SHORT_TIME_FORMAT) &&
      props.value !== state.prevValue
    ) {
      return {
        ...state,
        prevValue: props.value,
        isValid: true,
        time: formatTimeForInput(0),
      }
    }
    return null
  }

  handleChange = (e) => {
    const { currentTarget } = e
    const { value } = currentTarget

    if (!isNumeric(value)) return

    const { time } = this.state

    const { hours, minutes, seconds } = destructureTimeValue(value)
    const apiValue = formatTimeForApi(hours, minutes, seconds)

    if (!this.userCanUpdate(apiValue)) return

    if (this.props.useGeneralShortTime) {
      this.props.onChange(
        `${hours || `00`}:${minutes || `00`}:${seconds || `00`}`
      )
    } else {
      this.props.onChange(apiValue)
    }

    const apiTimeIsValid = this.determineValidity(apiValue)

    // eslint-disable-next-line no-bitwise
    if (this.state.isValid ^ apiTimeIsValid) {
      this.props.onValidityChange(
        apiTimeIsValid,
        'timer',
        strings.timer_error_msg(this.props.name)
      )
    }

    // remove or prepend '0' to string
    const reFormattedTime =
      value.length > time.length ? drop(1, value) : concat('0', value)

    this.setState(
      {
        time: reFormattedTime,
        isValid: apiTimeIsValid,
      },
      this.setCaretPosition
    )
  }

  determineValidity = (val) => {
    if (!val) return true

    return val <= CHEC_MAX_VALUE
  }

  userCanUpdate = (val) => {
    if (!val) return true

    return val <= MAX_TIMER_VALUE
  }

  setCaretPosition = () => {
    const elem = this.inputTimerRef.current
    const caretPos = this.state.time.length

    elem.setSelectionRange(caretPos, caretPos)
  }

  onFocus = () => {
    this.setState({ focused: true }, this.setCaretPosition)
    this.inputTimerRef.current.focus()
  }

  onBlur = () => {
    this.setState({ focused: false })
  }

  render() {
    const {
      size,
      label,
      hint,
      className,
      required,
      vertical,
      tooltip,
      id,
    } = this.props
    const { time, focused } = this.state

    const maxValue = this.props.maxValue || CHEC_MAX_VALUE

    const { hours, minutes, seconds } = destructureTimeValue(time)

    const hoursVisible = Number(hours) > 0
    const minutesVisible = hoursVisible || Number(minutes) > 0
    const secondsVisible = minutesVisible || Number(seconds) > 0

    const sizeClassName = () => {
      if (size === 'small') return 'ant-input-sm'
      if (size === 'large') return 'ant-input-lg'
      return null
    }

    return (
      <div
        className={classNames(
          'form-field',
          'input-timer-container',
          sizeClassName(size),
          className
        )}
      >
        {label && (
          <InputLabel
            label={label}
            hint={hint}
            tooltip={tooltip}
            required={required}
            size={size}
            vertical={vertical}
          />
        )}
        <span
          className={classNames('custom-timer-input', 'ant-input', {
            focused,
          })}
          onClick={this.onFocus}
        >
          <input
            className="hidden"
            onBlur={this.onBlur}
            onChange={this.handleChange}
            autoComplete="off"
            ref={this.inputTimerRef}
            id={id ? `#${id}` : '#input'}
            value={time}
          />
          <span
            className={classNames('hours', {
              visible: hoursVisible,
            })}
          >
            <span>{hours}</span>
            <span>{' h  '}</span>
          </span>
          <span
            className={classNames('mins', {
              visible: minutesVisible,
            })}
          >
            <span>{minutes}</span>
            <span>{' m  '}</span>
          </span>
          <span
            className={classNames('seconds', {
              visible: secondsVisible,
            })}
          >
            <span>{`${seconds} s`}</span>
          </span>
        </span>
        {!this.state.isValid && (
          <span className="error-msg">
            {strings.timer_error_msg(
              this.props.name,
              '0',
              strings.formattedTime(parseHmsStrings(maxValue))
            )}
          </span>
        )}
      </div>
    )
  }
}

InputTimer.defaultProps = {
  label: null,
  hint: null,
  className: null,
  onValidityChange: () => {},
}

InputTimer.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func,
  maxValue: PropTypes.number,
  onValidityChange: PropTypes.func,
  name: PropTypes.string.isRequired,
  size: PropTypes.string,
  label: PropTypes.string,
  hint: PropTypes.string,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  required: PropTypes.bool,
  vertical: PropTypes.bool,
  tooltip: PropTypes.string,
  useGeneralShortTime: PropTypes.bool,
  id: PropTypes.string,
}
